import React from "react";
import PropTypes from "prop-types";

/**
 * Formats a plan's limits into a summary string
 */
function formatLimits(plan) {
  const parts = [];
  if (plan.limits.widgets === null) {
    parts.push("Unlimited W");
  } else {
    parts.push(`${plan.limits.widgets}W`);
  }
  if (plan.limits.knowledgeSources === null) {
    parts.push("Unlimited KS");
  } else {
    parts.push(`${plan.limits.knowledgeSources}KS`);
  }
  return parts.join(" / ");
}

/**
 * Formats a plan's monthly credit allocations for display.
 * [B3 FIX] Distinguishes between null (unlimited → ∞) and undefined/missing
 * (no allocation configured → "—") for pre-credit plans.
 */
function formatCredits(plan) {
  if (!plan.monthlyCredits) return "—";
  const mc = plan.monthlyCredits;
  const fmt = (val) => {
    if (val === null) return "\u221e";
    if (val === undefined) return "—";
    return val.toLocaleString();
  };
  return `H:${fmt(mc.haiku)} / S:${fmt(mc.sonnet)} / O:${fmt(mc.opus)}`;
}

/**
 * Formats a plan's price for display
 */
function formatPrice(plan) {
  if (plan.price === null) return "Custom";
  if (plan.price === 0) return "Free";
  return `$${plan.price}/mo`;
}

/**
 * Table displaying all plans for admin management
 */
const PlanTable = ({
  plans,
  onEdit,
  onDelete,
  onToggleStatus,
  onManageInvites,
  togglingId,
}) => {
  return (
    <div className="admin-table-wrapper">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>ID</th>
            <th>Price</th>
            <th>Limits</th>
            <th>Credits/mo</th>
            <th>Order</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {plans.map((plan) => (
            <tr
              key={plan.id}
              className={plan.isActive ? "" : "admin-row-inactive"}
            >
              <td>
                <strong>{plan.name}</strong>
                {plan.isFree && (
                  <span className="admin-badge admin-badge-system">System</span>
                )}
                {plan.isPrivate && (
                  <span className="admin-badge admin-badge-private">
                    Private
                  </span>
                )}
              </td>
              <td>
                <code>{plan.id}</code>
              </td>
              <td>{formatPrice(plan)}</td>
              <td className="admin-limits-cell">{formatLimits(plan)}</td>
              <td className="admin-limits-cell">{formatCredits(plan)}</td>
              <td>{plan.sortOrder}</td>
              <td>
                <button
                  className={`admin-status-toggle ${plan.isActive ? "admin-status-active" : "admin-status-inactive"}`}
                  onClick={() => onToggleStatus(plan.id, !plan.isActive)}
                  disabled={plan.isFree || togglingId === plan.id}
                  title={
                    plan.isFree
                      ? "Cannot deactivate the free plan"
                      : plan.isActive
                        ? "Deactivate (grandfather existing users)"
                        : "Reactivate plan"
                  }
                >
                  {togglingId === plan.id
                    ? "..."
                    : plan.isActive
                      ? "Active"
                      : "Inactive"}
                </button>
              </td>
              <td className="admin-actions-cell">
                <button
                  className="admin-btn admin-btn-edit"
                  onClick={() => onEdit(plan)}
                  title="Edit plan"
                >
                  Edit
                </button>
                {plan.isPrivate && (
                  <button
                    className="admin-btn admin-btn-invites"
                    onClick={() => onManageInvites(plan)}
                    title="Manage invites"
                  >
                    Invites
                  </button>
                )}
                <button
                  className="admin-btn admin-btn-delete"
                  onClick={() => onDelete(plan)}
                  disabled={plan.isFree}
                  title={
                    plan.isFree ? "Cannot delete the free plan" : "Delete plan"
                  }
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

PlanTable.propTypes = {
  plans: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      price: PropTypes.number,
      limits: PropTypes.object.isRequired,
      sortOrder: PropTypes.number.isRequired,
      isActive: PropTypes.bool.isRequired,
      isFree: PropTypes.bool.isRequired,
    }),
  ).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onToggleStatus: PropTypes.func.isRequired,
  onManageInvites: PropTypes.func.isRequired,
  togglingId: PropTypes.string,
};

export default PlanTable;
